import React, { useMemo } from 'react';

import { di } from 'react-magnetic-di';

import { Inline, Stack } from '@atlaskit/primitives/compiled';
import type { AgentCreatorType } from '@atlaskit/rovo-agent-components/common/types';

import { InlineIconText } from '../inline-icon-text/index.tsx';
import { KnowledgeSourcePill } from '../knowledge-source-pill/index.tsx';

export type Knowledge = {
	enabled?: boolean | null;
	limit?: number;
	sources?:
		| {
				cloudId?: string | null;
				enabled?: boolean | null;
				filters?: {
					helpCenterAri?: string | null;
					jsmProjectFilter?: string[] | null;
					parentFilter?: string[] | null;
					projectFilter?: string[] | null;
					spaceFilter?: string[] | null;
				} | null;
				resourceType?: string | null;
				source: string;
		  }[]
		| null;
};

const NUMBER_OF_SOURCES_TO_SHOW = 5;

type KnowledgePillListProps = {
	cloudId: string;
	elemAfter?: (knowledge: {
		ari?: string;
		cloudId?: string | null;
		resourceType?: string | null;
		source: string;
	}) => React.ReactNode;
	knowledge?: Knowledge[];
	limit?: number;
	preferDirectConnectorFetch?: boolean;
	agentCreatorType?: AgentCreatorType;
};

export const KnowledgePillList = ({
	knowledge,
	cloudId,
	elemAfter,
	limit = NUMBER_OF_SOURCES_TO_SHOW,
	preferDirectConnectorFetch,
	agentCreatorType,
}: KnowledgePillListProps): JSX.Element => {
	di();

	const enabledKnowledge = useMemo(() => {
		return knowledge?.filter((edge) => edge.enabled);
	}, [knowledge]);

	const allEnabledFilters = useMemo(() => {
		return (
			enabledKnowledge
				?.flatMap((edge) => edge.sources)
				.filter((source): source is NonNullable<typeof source> => source?.enabled ?? false)
				.reduce(
					(
						acc: {
							ari?: string;
							cloudId?: string | null;
							resourceType?: string | null;
							source: string;
						}[],
						source,
					) => {
						const aris = [
							...(source?.filters?.parentFilter ?? []),
							...(source?.filters?.projectFilter ?? []),
							...(source?.filters?.jsmProjectFilter ?? []),
							...(source?.filters?.spaceFilter ?? []),
							...(source?.filters?.helpCenterAri ? [source.filters.helpCenterAri] : []),
						];

						const sourceCloudId = source.cloudId ?? null;

						aris.forEach((ari) => {
							if (
								!acc.some(
									(f) => f.ari === ari && f.source === source.source && f.cloudId === sourceCloudId,
								)
							) {
								acc.push({
									source: source.source,
									ari,
									resourceType: source.resourceType,
									cloudId: sourceCloudId,
								});
							}
						});

						return acc;
					},
					[],
				) ?? []
		);
	}, [enabledKnowledge]);

	const allEnabledSources = useMemo(() => {
		const allEnabledSources =
			enabledKnowledge
				?.flatMap((edge) => edge.sources)
				.filter((source): source is NonNullable<typeof source> => {
					const sourceCloudId = source?.cloudId ?? null;
					// If confluence or jira is enabled with no filters
					// it means that the knowledge is scoped to all projects / content
					if (
						!source?.enabled ||
						(['confluence', 'jira', 'jsm'].includes(source.source) &&
							allEnabledFilters.some(
								(f) => f.source === source.source && f.cloudId === sourceCloudId,
							))
					) {
						return false;
					}

					return true;
				})
				.reduce(
					(
						acc: { cloudId?: string | null; resourceType?: string | null; source: string }[],
						source,
					) => {
						const sourceCloudId = source.cloudId ?? null;
						if (!acc.some((s) => s.source === source.source && s.cloudId === sourceCloudId)) {
							acc.push({
								source: source.source,
								resourceType: source.resourceType,
								cloudId: sourceCloudId,
							});
						}

						return acc;
					},
					[],
				) ?? [];
		return allEnabledSources;
	}, [allEnabledFilters, enabledKnowledge]);
	const allCombinedSources = useMemo(() => {
		return allEnabledFilters.concat(allEnabledSources);
	}, [allEnabledFilters, allEnabledSources]);

	const showMoreEnabled = limit > 0;

	const firstFiveSources = useMemo(
		() => allCombinedSources.slice(0, limit > 0 ? limit : undefined),
		[allCombinedSources, limit],
	);

	return (
		<Stack space="space.050">
			{firstFiveSources.map((source, index) => {
				const isLastSource = index === NUMBER_OF_SOURCES_TO_SHOW - 1;
				return (
					<Inline key={`knowledge-source-${index}`} spread="space-between" alignBlock="center">
						<Inline space="space.100" alignBlock="center">
							<KnowledgeSourcePill
								ari={source.ari}
								source={source.source}
								cloudId={source.cloudId || cloudId}
								preferDirectConnectorFetch={preferDirectConnectorFetch}
								agentCreatorType={agentCreatorType}
							/>
							{isLastSource &&
								allCombinedSources.length > NUMBER_OF_SOURCES_TO_SHOW &&
								showMoreEnabled && (
									<InlineIconText
										content={`+ ${allCombinedSources.length - NUMBER_OF_SOURCES_TO_SHOW}`}
										fullWidth
									/>
								)}
						</Inline>

						{elemAfter?.(source)}
					</Inline>
				);
			})}
		</Stack>
	);
};
